import { Alert, Portal, Snackbar } from "@mui/material";
import { useWebgate } from "@xvia/webgate-connect";
import { FC, useCallback, useEffect, useState } from "react";
import { Element } from "react-scroll";
import { getApps } from "../apis/relevant-catalog";
import { Catalog } from "../apis/search-catalog";
import { AppsContainer } from "../modules/apps/apps.container";
import { CategoryBannerContainer } from "../modules/category/category-banner.container";
import { DownloadAppMobile } from "../modules/downloadAppMobile/downloadAppMobile";
import { FavoriteServicesContainer } from "../modules/favorite-services/favorite-services.container";
import { HeaderContainer } from "../modules/header/header.container";
import { IndicatorsSectionContainer } from "../modules/indicators-section/indicators-section.container";
import { MainBannerContainer } from "../modules/main-banner/main-banner.container";
import { OmbudsmanBannerContainer } from "../modules/ombudsman-banner/ombudsman-banner.container";
import { PrismaLabBannerContainer } from "../modules/prisma-lab-banner/prisma-lab-banner.container";
import { ProfileRecommendationsContainer } from "../modules/profile-recommendations/profile-recommendations.container";
import { PublicRelatedCards } from "../modules/public-related-cards/public-related-cards";
import { RelevantServicesContainer } from "../modules/relevant-services/relevant-services.container";
import { SearchContainer } from "../modules/search/search.container";
import { SocialNetworkContainer } from "../modules/social-network/social-network.container";
import { TopBar } from "../modules/top-bar/top-bar";
import { TopServicesContainer } from "../modules/top-services/top-services.container";
import { UserRelatedWidgetsContainer } from "../modules/user-related-widgets/user-related-widgets.container";
import { WhatsappBannerContainer } from "../modules/whatsapp-banner/whatsapp-banner.container";
import { CategoryBannerContextProvider } from "../providers/category-banner-provider";
import {
  MunicipalityHomeProvider,
  useMunicipalityHome,
} from "../providers/municipality-home-provider";
import "./Home.css";
import { MunicipalityHomeBar } from "../modules/municipality-home-bar/municipality-home-bar";
import { OiaRiscoBannerContainer } from "../modules/oia-risco-banner/oia-risco-banner.container";

const HomeContent: FC = () => {
  const [allCatalogList, setAllCatalogList] = useState<Catalog[]>();
  const { userInfo } = useWebgate();
  const {
    municipalityName,
    municipioQuerySuffix,
    isMunicipalityHome,
    codIbgeMunicipio,
  } = useMunicipalityHome();
  const [favoriteList, setFavoriteList] = useState<string[]>([]);
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [messageAlert, setMessageAlert] = useState<string>("");
  const [severityAlert, setSeverityAlert] = useState<"success" | "error">(
    "success"
  );

  useEffect(() => {
    if (userInfo) {
      if (userInfo.catalog_favorite) {
        setFavoriteList(JSON.parse(userInfo?.catalog_favorite));
      }
    }
  }, [setFavoriteList, userInfo]);

  const getAllApps = useCallback(() => {
    getApps(codIbgeMunicipio).then((regularServices) => {
      setAllCatalogList(regularServices);
    });
  }, [codIbgeMunicipio]);

  useEffect(() => {
    getAllApps();
  }, [getAllApps]);

  const updateFavorites = useCallback((slug: string, remove: boolean) => {
    setFavoriteList((prevList) => {
      if (remove) {
        return prevList.filter((item) => item !== slug);
      } else {
        return [...prevList, slug];
      }
    });
  }, []);

  return (
    <div id="wrapper">
      <TopBar />
      <CategoryBannerContextProvider>
        <Portal>
          <Snackbar
            sx={{ zIndex: 9999999 }}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            open={openAlert}
            autoHideDuration={3000}
            onClose={() => setOpenAlert(false)}
          >
            <Alert
              onClose={() => setOpenAlert(false)}
              severity={severityAlert}
              sx={{ width: "100%" }}
            >
              {messageAlert}
            </Alert>
          </Snackbar>
        </Portal>
        <MainBannerContainer>
          <HeaderContainer />
          <SearchContainer className="xvia-home-search" />
        </MainBannerContainer>
        <MunicipalityHomeBar municipalityName={municipalityName} />

        <Element name="serviceByCategory">
          <PublicRelatedCards
            municipalityName={municipalityName}
            municipalityCode={codIbgeMunicipio}
          />
        </Element>
        <Element name="userRelatedWidgets">
          <UserRelatedWidgetsContainer />
        </Element>

        <Element name="oiaRiscoBanner">
          <OiaRiscoBannerContainer />
        </Element>

        {userInfo && (
          <Element name="favoriteServices">
            <FavoriteServicesContainer
              allCatalogList={allCatalogList || []}
              favoriteList={favoriteList || []}
              onFavoriteUpdate={updateFavorites}
              setMessageAlert={setMessageAlert}
              setSeverityAlert={setSeverityAlert}
              setOpenAlert={setOpenAlert}
              municipioQuerySuffix={municipioQuerySuffix}
            />
          </Element>
        )}

        <Element name="relevantServices">
          <RelevantServicesContainer
            allCatalogList={allCatalogList || []}
            favoriteList={favoriteList || []}
            onFavoriteUpdate={updateFavorites}
            setMessageAlert={setMessageAlert}
            setSeverityAlert={setSeverityAlert}
            setOpenAlert={setOpenAlert}
            codIbgeMunicipio={codIbgeMunicipio}
            municipalityName={municipalityName}
            municipioQuerySuffix={municipioQuerySuffix}
            isMunicipalityHome={isMunicipalityHome}
          />
        </Element>

        <Element name="topServices">
          <TopServicesContainer
            allCatalogList={allCatalogList || []}
            favoriteList={favoriteList || []}
            onFavoriteUpdate={updateFavorites}
            setMessageAlert={setMessageAlert}
            setSeverityAlert={setSeverityAlert}
            setOpenAlert={setOpenAlert}
            codIbgeMunicipio={codIbgeMunicipio}
            municipalityName={municipalityName}
            municipioQuerySuffix={municipioQuerySuffix}
            isMunicipalityHome={isMunicipalityHome}
          />
        </Element>

        {userInfo?.preferred_username && (
          <Element name="profileRecommendations">
            <ProfileRecommendationsContainer
              allCatalogList={allCatalogList || []}
              favoriteList={favoriteList || []}
              onFavoriteUpdate={updateFavorites}
              setMessageAlert={setMessageAlert}
              setSeverityAlert={setSeverityAlert}
              setOpenAlert={setOpenAlert}
            />
          </Element>
        )}

        <Element name="appsContainer">
          <AppsContainer />
        </Element>
        <Element name="category">
          <CategoryBannerContainer
            municipioQuerySuffix={municipioQuerySuffix}
            municipalityName={municipalityName}
            isMunicipalityHome={isMunicipalityHome}
          />
        </Element>
        <Element name="indicators">
          <IndicatorsSectionContainer />
        </Element>
        <Element name="downloadAppMobile">
          <DownloadAppMobile />
        </Element>
        <Element name="banners">
          <div className="xvia-banners-wrapper">
            <WhatsappBannerContainer />
            <PrismaLabBannerContainer />
          </div>
        </Element>
        <Element name="ombudsmanBanner">
          <OmbudsmanBannerContainer />
        </Element>
        {/* <Element name="socialNetwork">
          <SocialNetworkContainer />
        </Element> */}
      </CategoryBannerContextProvider>
    </div>
  );
};

export const Home: FC = () => {
  return (
    <MunicipalityHomeProvider>
      <HomeContent />
    </MunicipalityHomeProvider>
  );
};
